import { createApiRef, IdentityApi } from '@backstage/core-plugin-api';
import type { FormInputs } from '@runway/plugin-orion-auto-backend';
import { timer } from '@runway/devkit';
import { RestClientApi } from '@runway/devkit-common';

type OrionServiceResponse = {
  response: string;
};

export interface OrionServiceApi {
  updateDataFromForm(formParams: FormInputs): Promise<OrionServiceResponse>;

  addEntity(entityParams: {
    partitionKey: Date;
    rowKey: string;
    name: string;
    ID: string;
  }): Promise<OrionServiceResponse>;

  getAppCodes(): Promise<any>;

  getDatabricksClusters(workspaceName: any): Promise<any>;
  getDatabricksClusterPermissions(clusterName: any, workspaceName: any): Promise<any>;
  listMembersInAdGroup(data: any): Promise<any>;
  adbGrantManagePermission(data: any): Promise<any>;
  createDeaEdpIssue(params: { requestor: string; summary: string; details: string }): Promise<any>;
  provisionAMLComputeInstance(inputs: any): Promise<any>;
  getResourceGroupsInSubscription(subscriptionId: any): Promise<any>;
  getAMLWorkspacesInResourceGroup(resourceGroup: any): Promise<any>;
  provisionAZKeyVault(params: { resourceGroup: string; aaId: string }): Promise<void>;
  provisionAMLComputeCluster(inputs: {
    clusterName: string;
    subscription: string;
    resourceGroup: string;
    amlWorkspace: string;
    maxNodes: string;
    vmSKU: string;
    clusterAvailability: string;
  }): Promise<void>;
}

export const orionServiceApiRef = createApiRef<OrionServiceApi>({
  id: 'plugin.orion-auto.orion.service',
});

export default class OrionService implements OrionServiceApi {
  constructor(
    private backendUrl: string,
    private identityApi: IdentityApi,
    private restClient: RestClientApi,
  ) {}

  getHeaders = async (): Promise<Record<string, string>> => {
    const { token } = await this.identityApi.getCredentials();
    return { Authorization: `Bearer ${token}` };
  };

  updateDataFromForm = async (formParams: FormInputs) => {
    const headers = await this.getHeaders();
    const res = await this.restClient.put(
      `${this.backendUrl}/api/orion-auto/updateData`,
      {
        ...formParams,
      },
      { headers },
    );
    if (res.status !== 200) {
      throw new Error('Failed to update data');
    }
    let counter = 1;
    let jobUrl = 'no match';
    const commitSHA = res.data.response;
    while (jobUrl === 'no match' && counter < 15) {
      await timer(5000);
      const adoLink = await this.restClient.get(
        `${this.backendUrl}/api/orion-auto/getPipeline?pipelineSha=${commitSHA}`,
        {
          headers,
        },
      );
      jobUrl = adoLink.data.response;
      counter++;
    }
    if (jobUrl === 'no match') {
      throw new Error('Job URL not found');
    }
    return { response: jobUrl };
  };

  addEntity = async (entityParams: { partitionKey: Date; rowKey: string; name: string; ID: string }) => {
    const headers = await this.getHeaders();
    const res = await this.restClient.post(
      `${this.backendUrl}/api/orion-auto/addEntity`,
      {
        ...entityParams,
      },
      { headers },
    );

    if (res.status !== 200) {
      throw new Error('Failed to add entity');
    }

    return { response: res.data };
  };

  getAppCodes = async (): Promise<any> => {
    const headers = await this.getHeaders();
    const result = await this.restClient.get(`${this.backendUrl}/api/proxy/apigee-prod/orion-platform/appcodes`, {
      headers,
    });
    if (result.status !== 200) {
      throw new Error('Failed to retrieve app codes');
    }
    return result.data;
  };

  getDatabricksClusters = async (workspaceName: any): Promise<any> => {
    const headers = await this.getHeaders();
    const result = await this.restClient.get(
      `${this.backendUrl}/api/proxy/apigee-prod/orion-platform/databricks/list_all_clusters?adb_workspace_name=${workspaceName}`,
      {
        headers,
      },
    );
    if (result.status !== 200) {
      throw new Error('Failed to retrieve databricks clusters');
    }
    return result.data;
  };

  getDatabricksClusterPermissions = async (clusterName: any, workspaceName: any): Promise<any> => {
    const headers = await this.getHeaders();
    const result = await this.restClient.get(
      `${this.backendUrl}/api/proxy/apigee-prod/orion-platform/databricks/get_cluster_permissions?cluster_name=${clusterName}&adb_workspace_name=${workspaceName}`,
      {
        headers,
      },
    );
    if (result.status !== 200) {
      throw new Error('Failed to retrieve cluster permissions');
    }
    return result.data;
  };

  listMembersInAdGroup = async (data: any) => {
    try {
      const headers = await this.getHeaders();
      const url = `${this.backendUrl}/api/proxy/apigee-prod/orion-platform/dra/list_members_in_AD_group`;
      const result = await this.restClient.post(url, data, { headers });
      return result.data;
    } catch (error) {
      throw new Error('There was an error getting members.');
    }
  };

  adbGrantManagePermission = async (data: any) => {
    try {
      const headers = await this.getHeaders();
      const url = `${this.backendUrl}/api/proxy/apigee-prod/orion-platform/grant_adb_permission`;
      const result = await this.restClient.post(url, data, { headers });
      return result.data;
    } catch (error) {
      throw new Error('There was an error granting access.');
    }
  };

  createDeaEdpIssue = async (params: { requestor: string; summary: string; details: string }) => {
    const headers = await this.getHeaders();
    const res = await this.restClient.post(`${this.backendUrl}/api/orion-auto/createDeaEdpIssue`, params, { headers });

    if (res.status !== 200) {
      throw new Error(`Failed to create issue: ${res.statusText}`);
    }

    return { response: res.data };
  };

  provisionAMLComputeInstance = async (params: any): Promise<any> => {
    const headers = await this.getHeaders();
    const res = await this.restClient.post(`${this.backendUrl}/api/orion-auto/provisionAMLComputeInstance`, params, {
      headers,
    });
    return res;
  };

  provisionAMLComputeCluster = async (params: {
    amlWorkspace: string;
    clusterAvailability: string;
    clusterName: string;
    maxNodes: string;
    resourceGroup: string;
    subscription: string;
    vmSKU: string;
  }): Promise<void> => {
    const headers = await this.getHeaders();
    await this.restClient.post(`${this.backendUrl}/api/orion-auto/provisionAMLComputeCluster`, params, { headers });
  };

  getResourceGroupsInSubscription = async (subscriptionId: string): Promise<any> => {
    const headers = await this.getHeaders();
    const result = await this.restClient.get(
      `${this.backendUrl}/api/proxy/apigee-prod/orion-platform/arm/resource_groups_arm/subscription/${subscriptionId}`,
      {
        headers,
      },
    );
    return result.data;
  };

  getAMLWorkspacesInResourceGroup = async (resourceGroup: string): Promise<any> => {
    const headers = await this.getHeaders();
    const result = await this.restClient.get(
      `${this.backendUrl}/api/proxy/apigee-prod/orion-platform/arm/resource_groups_arm/${resourceGroup}/resources/aml`,
      {
        headers,
      },
    );
    return result.data;
  };

  provisionAZKeyVault = async ({
    subscription,
    aaId,
    resourceGroup,
  }: {
    aaId: string;
    resourceGroup: string;
    subscription: string;
  }): Promise<void> => {
    const headers = await this.getHeaders();

    let subscriptionName = 'aa-ba-prod-spoke';
    if (subscription !== 'c450f3d1-583c-495f-b5d3-0b38b99e70c0') {
      subscriptionName = 'aa-ba-nonprod-spoke';
    }

    const rgInfo = await this.restClient.get(
      `${this.backendUrl}/api/proxy/apigee-prod/orion-platform/arm/resource_groups_arm/${resourceGroup}`,
      { headers },
    );
    const environment = rgInfo.data.tags['aa-sdlc-environment'][0];
    const location = resourceGroup.split('-')[2];
    const requestor = String(aaId);

    const body = {
      aa_application: rgInfo.data.tags['aa-application'],
      environment,
      aa_location: `${location[1]}${location[2]}st${location[3]}${location[4]}`,
      subscription: {
        name: subscriptionName,
        subscription_id: subscription,
        environment,
        code: 'ba',
      },
      requestor,
      testing: false,
      resources: {
        idkv: {
          keyVaultRgName: resourceGroup,
          keyVaultName: `ba-${environment}-${location}-${requestor}-kv`,
          employeeIdKv: requestor,
          keyVaultType: 'AAID',
        },
      },
    };

    await this.restClient.post(`${this.backendUrl}/api/proxy/apigee-prod/orion-platform/orchestrator/create`, body, {
      headers,
    });
  };
}
